import { useCallback, useState } from 'react';

import { IRowNode } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { FertilizationWithEmbryos } from '@/rtk-query.gql';

import { SelectionService } from '../../utils/selectionService';
import { useFreezeContext } from '../useFreezeContext';

export const useDaySelection = (
  gridRef: React.RefObject<AgGridReact>,
  fertEmbryosData: FertilizationWithEmbryos[],
) => {
  const [lastSelectedDay, setLastSelectedDay] = useState<7 | 65 | null>(null);
  const { detailGridRefs } = useFreezeContext();

  const handleCheckDay = useCallback(
    (day: 7 | 65) => {
      const allEmbryoIds: string[] = [];
      const fertIdsWithDay: Set<string> = new Set();

      const filteredFertIds = new Set<string>();
      gridRef.current?.api?.forEachNodeAfterFilter((node: IRowNode) => {
        filteredFertIds.add(node.data.fertId);
      });

      fertEmbryosData.forEach((fert) => {
        if (filteredFertIds.has(fert.fertId as string)) {
          const dayEmbs =
            fert.liveEmbryos?.filter((emb) =>
              day === 7 ? emb.d7 === 1 : emb.d65 === 1,
            ) || [];
          if (dayEmbs.length > 0) {
            fertIdsWithDay.add(fert.fertId as string);
            allEmbryoIds.push(...dayEmbs.map((emb) => emb.id as string));
          }
        }
      });

      if (lastSelectedDay === day) {
        SelectionService.updateSelection('live', []);
        SelectionService.updateSelection('temp', []);
        SelectionService.updateSelection('fert', []);
        setLastSelectedDay(null);

        if (gridRef.current?.api) {
          gridRef.current.api.deselectAll();
          Object.values(detailGridRefs).forEach((refs) => {
            if (refs?.liveEmbryosGridRef.current?.api) {
              refs.liveEmbryosGridRef.current.api.deselectAll();
            }
          });
        }
      } else {
        SelectionService.updateSelection('live', allEmbryoIds);
        SelectionService.updateSelection('fert', Array.from(fertIdsWithDay));
        setLastSelectedDay(day);

        if (gridRef.current?.api) {
          gridRef.current.api.deselectAll();
          gridRef.current.api.forEachNode((node) => {
            if (fertIdsWithDay.has(node.data.fertId)) {
              node.setSelected(true);
              const gridRefs = detailGridRefs[node.data.fertId];
              if (gridRefs?.liveEmbryosGridRef.current?.api) {
                gridRefs.liveEmbryosGridRef.current.api.forEachNode(
                  (detailNode) => {
                    const isSelected = allEmbryoIds.includes(
                      detailNode.data.id,
                    );
                    detailNode.setSelected(isSelected);
                  },
                );
              }
            }
          });
        }
      }
    },
    [lastSelectedDay, gridRef, fertEmbryosData, detailGridRefs],
  );

  return {
    lastSelectedDay,
    handleCheckDay,
  };
};
